import { Box, Grid } from '@mui/material';
import { useMemo } from 'react';
import { convertCentToFahr, convertKpaToPsi } from 'utils/helpers/unitSystem';
import TenWheelHaulTruck from '../Schematics/TenWheelHaulTruck';
import TirePressure from '../TirePressure';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { TireStatus, determineTireStatus } from 'utils/tpms';
import { useAppSelector } from 'store/hook';
import { YEAR_MONTH_DAY_TIME_FORMAT } from 'utils/constants/dateTime';

// 10x6 tri-axle: front single + two rear tandem dual axles. p-index order
// matches views/TirePressure/util.ts#getOrderedTireCodes for '10x6'.
const tireNumberSystem = [
  '0x17',
  '0x19',
  '0x27',
  '0x29',
  '0x26',
  '0x2A',
  '0x37',
  '0x39',
  '0x36',
  '0x3A',
];

export const TenTires = ({
  tireData,
  limits,
  enableDarkTheme,
  selectedAsset,
  batteryData,
  unitSystem,
  tireCodes,
  metricType,
  makeModel,
  onTireClick,
}: {
  tireData: any;
  limits: any;
  enableDarkTheme: boolean;
  selectedAsset: any;
  unitSystem: string;
  tireCodes: any;
  metricType: string;
  makeModel: string;
  batteryData: any;
  onTireClick?: (tireCode: string) => void;
}) => {
  const { fleetData } = useAppSelector((state) => state.tireProfileReducer);
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const lowerCaseMetricType = metricType.toLowerCase();

  const data = useMemo(() => {
    try {
      const isTemperature = lowerCaseMetricType === 'temperature';

      const fitmentData = tireData?.tireFitmentData ?? [];

      const latestAssetData =
        fleetData[
          lowerCaseMetricType === 'temperature' ? 'temperature' : 'pressure'
        ]?.[selectedAsset?.device?.deviceId] || {};

      const result: any = {};
      tireNumberSystem.forEach((code, index) => {
        const tireNumber = index + 1;

        const latestInfo = latestAssetData[code];
        const value = latestInfo?.value ?? null;

        const lastUpdated = latestInfo?.ts
          ? convertUtcToDisplayTime(
              new Date(latestInfo.ts).toISOString(),
              preferredTimeDisplayMode,
              timezone,
              YEAR_MONTH_DAY_TIME_FORMAT
            )
          : 'N/A';
        result[`p${tireNumber}_last_updated`] = lastUpdated;
        result[`p${tireNumber}_ts`] = latestInfo?.ts ?? null;
        let tireLimit =
          limits?.find((item: any) => item.tireCode === code) || {};

        const maxValue = isTemperature
          ? unitSystem === 'metric'
            ? tireLimit.maxTemperature
            : convertCentToFahr(tireLimit.maxTemperature)
          : unitSystem === 'metric'
          ? tireLimit.maxPressure
          : convertKpaToPsi(tireLimit?.maxPressure);
        const minValue = isTemperature
          ? unitSystem === 'metric'
            ? tireLimit.minTemperature
            : convertCentToFahr(tireLimit.minTemperature)
          : unitSystem === 'metric'
          ? tireLimit.minPressure
          : convertKpaToPsi(tireLimit?.minPressure);
        const warningValue = isTemperature
          ? unitSystem === 'metric'
            ? tireLimit.warningTemperature
            : convertCentToFahr(tireLimit.warningTemperature)
          : unitSystem === 'metric'
          ? tireLimit.warningPressure
          : convertKpaToPsi(tireLimit?.warningPressure);

        const fitmentInfo = fitmentData.find(
          (item: any) => item.tireCode === code
        );

        const fitmentDate = fitmentInfo?.installedAt
          ? convertUtcToDisplayTime(
              fitmentInfo.installedAt,
              preferredTimeDisplayMode,
              timezone,
              'DD MMM YYYY'
            )
          : 'Not Set';

        tireLimit = {
          ...tireLimit,
          minValue,
          maxValue,
        };

        result[`p${tireNumber}`] = value;
        result[`p${tireNumber}Limits`] = tireLimit;
        result[`p${tireNumber}_fitment_date`] = fitmentDate;
        result[`p${tireNumber}_serial_number`] =
          fitmentInfo?.serialNumber || null;
        result[`${tireNumber}HasSensor`] = fitmentInfo ?? false;

        const status = determineTireStatus(
          value,
          { min: minValue, max: maxValue, warning: warningValue },
          !!fitmentInfo,
          lowerCaseMetricType as 'pressure' | 'temperature'
        );
        result[`p${tireNumber}Status`] = status;

        const isOverMaxRes = status === TireStatus.UNHEALTHY;
        result[`isP${tireNumber}WarninigMax`] = isOverMaxRes;
      });

      result.tireStatusMap = {
        p1: result.p1Status,
        p2: result.p2Status,
        p3: result.p3Status,
        p4: result.p4Status,
        p5: result.p5Status,
        p6: result.p6Status,
        p7: result.p7Status,
        p8: result.p8Status,
        p9: result.p9Status,
        p10: result.p10Status,
      };

      const checkIsStale = (ts: number | null | undefined) => {
        if (!ts) return false;
        const hoursDiff = (Date.now() - ts) / (1000 * 60 * 60);
        return hoursDiff > 24;
      };

      result.tireStaleMap = {
        p1: checkIsStale(result.p1_ts),
        p2: checkIsStale(result.p2_ts),
        p3: checkIsStale(result.p3_ts),
        p4: checkIsStale(result.p4_ts),
        p5: checkIsStale(result.p5_ts),
        p6: checkIsStale(result.p6_ts),
        p7: checkIsStale(result.p7_ts),
        p8: checkIsStale(result.p8_ts),
        p9: checkIsStale(result.p9_ts),
        p10: checkIsStale(result.p10_ts),
      };

      return result;
    } catch (error) {
      console.log(error);
      return {};
    }
  }, [
    limits,
    lowerCaseMetricType,
    makeModel,
    tireData,
    unitSystem,
    fleetData,
    selectedAsset,
  ]);

  const slowLeaks = tireData.tireCurrentStatus?.slowLeaks ?? [];
  const fastLeaks = tireData.tireCurrentStatus?.fastLeaks ?? [];

  const deviceBattery = selectedAsset?.device?.deviceId
    ? batteryData?.[selectedAsset.device.deviceId]
    : undefined;

  const getBatteryStatus = (tireCode: string) => {
    return deviceBattery?.[tireCode]?.isBatteryLow || false;
  };

  const batteryLow = tireNumberSystem.reduce(
    (acc: Record<string, boolean>, code, index) => {
      acc[`p${index + 1}`] = getBatteryStatus(code);
      return acc;
    },
    {}
  );

  return (
    <>
      <Grid item lg={3}>
        {[1, 3, 5, 7, 9].map((item, index) => (
          <TirePressure
            key={index}
            enableDarkTheme={enableDarkTheme}
            value={data[`p${item}`]}
            limit={data[`p${item}Limits`]}
            isAlert={data[`isP${item}WarninigMax`]}
            tireCode={tireNumberSystem[item - 1]}
            unitSystem={unitSystem}
            tireCodes={tireCodes}
            metricType={metricType}
            slowLeaks={slowLeaks}
            fastLeaks={fastLeaks}
            fitmentDate={data[`p${item}_fitment_date`]}
            hasSensor={data[`${item}HasSensor`]}
            isBatteryLow={getBatteryStatus(tireNumberSystem[item - 1])}
            serialNumber={data[`p${item}_serial_number`]}
            lastUpdated={data[`p${item}_last_updated`]}
          />
        ))}
      </Grid>
      <Grid item lg={6}>
        <Box
          sx={{
            textAlign: 'center',
            paddingTop: '15px',
            paddingBottom: '30px',
          }}
        >
          <TenWheelHaulTruck
            values={data}
            tireStatusMap={data.tireStatusMap}
            tireStaleMap={data.tireStaleMap}
            batteryLow={batteryLow}
            onTireClick={onTireClick}
            tireNumberSystem={tireNumberSystem}
          />
        </Box>
      </Grid>
      <Grid item lg={3}>
        {[2, 4, 6, 8, 10].map((item, index) => (
          <TirePressure
            key={index}
            enableDarkTheme={enableDarkTheme}
            value={data[`p${item}`]}
            limit={data[`p${item}Limits`]}
            isAlert={data[`isP${item}WarninigMax`]}
            tireCode={tireNumberSystem[item - 1]}
            unitSystem={unitSystem}
            tireCodes={tireCodes}
            metricType={metricType}
            slowLeaks={slowLeaks}
            fastLeaks={fastLeaks}
            fitmentDate={data[`p${item}_fitment_date`]}
            hasSensor={data[`${item}HasSensor`]}
            isBatteryLow={getBatteryStatus(tireNumberSystem[item - 1])}
            serialNumber={data[`p${item}_serial_number`]}
            lastUpdated={data[`p${item}_last_updated`]}
          />
        ))}
      </Grid>
    </>
  );
};
